// Companion/blocks/ChatBlocks.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatBlock } from '@/slices/chat/chatSlice';
import type { ChatBlockPlacement } from '@/types/chatStream';
import { MAX_CHAT_BLOCKS_PER_TURN } from '@/types/chatBlocks';
import type { ChatSurface } from '@/types/chatEnvelope';
import { useMcGrawHillSourceLabel } from '../../../../../mcgrawHill/mcgrawHillChapter'; // mcgraw-hill
import { resolveChatBlock, type ResolvedChatBlock } from './chatBlockRegistry';
import { ChatBlockVisibilityContext } from './chatBlockVisibility';
import type { ChatQuestionFollowup } from './chatQuestionFollowup';

export interface ChatBlocksProps {
  /** Every block of one message, in the order the turn served them. */
  blocks: ChatBlock[];
  /** Which slot is being drawn: above the answer, or after it. */
  placement: ChatBlockPlacement;
  surface: ChatSurface;
  /** A confirmed question answer belongs to this answer's follow-up chips. */
  onQuestionAnswered?: (question: ChatQuestionFollowup) => void;
  /** Preserve the thread's position before a block grows in place. */
  onBeforeContentExpand?: () => void;
}

interface ChatBlockViewProps {
  block: ChatBlock;
  /** Null when the kind is unknown or the payload will not parse. */
  resolved: ResolvedChatBlock | null;
  shouldFocus?: boolean;
  isHidden?: boolean;
}

/**
 * One block: a heading and the registry's body.
 *
 * A read-only block is inert rather than absent — a legacy conversation should
 * still read as the conversation it was, so the content is drawn and only the
 * interaction is taken away.
 */
const ChatBlockView = ({
  block,
  resolved,
  shouldFocus = false,
  isHidden = false,
}: ChatBlockViewProps) => {
  const isReadOnly = block.isLegacy;
  const isNavigation = block.kind === 'navigation';

  // The return row is a note about what the student did, not content the turn
  // served, so it is drawn as a bare line: no card, no header, no padding.
  const isEventLine =
    block.kind === 'set_result' ||
    // mcgraw-hill: a scripted answer's action row is a bare row too.
    block.kind === 'mcgraw_hill_actions';

  const isBare = isNavigation || isEventLine;

  /**
   * Whether the body sits in the fieldset's own padding. A study set pads
   * itself — its items area has padding and its footer is a single padded row
   * — so the fieldset adding a second layer around it is what made the block
   * twice as tall as it reads (ORA-681 review).
   */
  // mcgraw-hill: a quoted reference passage (only McGraw Hill answers quote
  // chat documents) draws its own header, like the figure card.
  const isMcGrawHillQuote =
    block.kind === 'document_excerpt' &&
    block.payload.source === 'chat_document';

  const isBareBody =
    isBare ||
    block.kind === 'study_set' ||
    // mcgraw-hill: the figure and quote cards fill the frame edge to edge.
    block.kind === 'mcgraw_hill_figure' ||
    isMcGrawHillQuote;

  /**
   * A renderer that draws its own header row gets no second one. A block that
   * failed to resolve keeps the generic header: the placeholder under it has
   * to say which block could not be drawn.
   */
  const ownsHeader = resolved?.ownsHeader === true || isMcGrawHillQuote;
  const formatSourceLabel = useMcGrawHillSourceLabel(); // mcgraw-hill
  const resolvedTitle = resolved?.title ?? 'Content';

  // mcgraw-hill: an excerpt's "Ch04.pdf · p. 1" reads "Chapter 4 · p. 1".
  const title =
    block.kind === 'document_excerpt'
      ? formatSourceLabel(resolvedTitle)
      : resolvedTitle;

  // Published to the renderer under it: a hidden block is mounted, and one whose
  // mount starts work (a draft question asks the server to write it) must not
  // start it for something nobody has opened. See `chatBlockVisibility.tsx`.
  const visibility = useMemo(() => ({ isHidden }), [isHidden]);
  /**
   * The kind kicker, dropped where the registry's title already names the kind
   * — "QUESTION  Practice question" said the same thing twice (ORA-628). A
   * block that failed to resolve keeps the kicker: "Content" names nothing.
   */
  const kicker = resolved?.isGenericTitle ? null : blockKindLabel(block.kind);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (shouldFocus) sectionRef.current?.focus();
  }, [shouldFocus]);

  return (
    <section
      ref={sectionRef}
      tabIndex={shouldFocus ? -1 : undefined}
      hidden={isHidden}
      aria-hidden={isHidden || undefined}
      className={
        isEventLine
          ? 'overflow-hidden'
          : 'overflow-hidden rounded-xl border border-border-subtle bg-surface-raised backdrop-blur-xl'
      }
      data-testid="chat-block"
      data-block-kind={block.kind}
      data-block-id={block.id}
    >
      {!isBare && !ownsHeader ? (
        <header className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
          <div className="flex min-w-0 items-center gap-1.5">
            {kicker ? (
              <span className="shrink-0 text-[10px] font-medium tracking-wide text-fg-secondary uppercase">
                {kicker}
              </span>
            ) : null}
            <strong className="min-w-0 truncate text-xs font-medium text-fg-primary">
              {title}
            </strong>
          </div>
          {isReadOnly ? (
            <span className="shrink-0 text-[10px] uppercase tracking-wide text-fg-secondary">
              Read-only
            </span>
          ) : null}
        </header>
      ) : null}
      {resolved ? (
        // A restored legacy block is drawn inside a disabled fieldset, which
        // disables every control under it for the pointer *and* the keyboard.
        // The ported components were built to answer, flip and submit, and an
        // archive must not write anything new; a CSS-only guard would still let
        // a tabbed-to answer option through.
        <fieldset
          className={`m-0 min-w-0 border-0 ${isBareBody ? 'p-0' : 'p-3'}`}
          disabled={isReadOnly || undefined}
          aria-disabled={isReadOnly || undefined}
        >
          <ChatBlockVisibilityContext.Provider value={visibility}>
            {resolved.body}
          </ChatBlockVisibilityContext.Provider>
        </fieldset>
      ) : (
        <p className="m-0 px-3 py-4 text-xs text-fg-secondary">
          This content can’t be shown here.
        </p>
      )}
    </section>
  );
};

const blockKindLabel = (kind: string) => {
  if (kind === 'question') return 'Question';
  if (kind === 'help_doc') return 'Help Doc';

  // Title Case, like every card's own header ("Document Excerpt").
  return kind
    .split('_')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
};

const payloadHasAnswer = (block: ChatBlock) =>
  block.kind === 'question' &&
  typeof block.payload.selectedLetter === 'string' &&
  block.payload.selectedLetter.trim() !== '';

/**
 * The blocks of one message, for one placement slot.
 *
 * Two rules live here rather than in any single renderer.
 *
 * The cap — at most three blocks per turn — counts only blocks that actually
 * resolved, so a kind this client cannot draw never evicts one it can. It is
 * applied to the message's whole list before the slot is filtered, so a turn
 * that serves four renderable blocks drops the fourth wherever it would have
 * gone rather than showing four across two slots. The server caps at three as
 * well (spec, "Serve tools"); this is the client holding the same line,
 * because the readability of the reply is a frontend promise.
 *
 * `placement` decides the slot: `inline` blocks sit above the answer, where
 * they arrive before the text does, and `end` blocks follow it.
 */
const ChatBlocks = ({
  blocks,
  placement,
  surface,
  onQuestionAnswered,
  onBeforeContentExpand,
}: ChatBlocksProps) => {
  const [openedBlockIds, setOpenedBlockIds] = useState<Set<string>>(
    () => new Set()
  );

  const [answeredBlockIds, setAnsweredBlockIds] = useState<Set<string>>(
    () => new Set(blocks.filter(payloadHasAnswer).map((block) => block.id))
  );

  const [focusBlockId, setFocusBlockId] = useState<string | null>(null);

  const updateAnswered = useCallback((blockId: string, answered: boolean) => {
    setAnsweredBlockIds((current) => {
      if (current.has(blockId) === answered) return current;

      const next = new Set(current);
      if (answered) next.add(blockId);
      else next.delete(blockId);
      return next;
    });
  }, []);

  let resolvedCount = 0;

  const shown = blocks
    .map((block) => ({
      block,
      resolved: resolveChatBlock(block, {
        surface,
        isReadOnly: block.isLegacy,
        onAnsweredChange: (answered, question) => {
          updateAnswered(block.id, answered);
          if (question) onQuestionAnswered?.(question);
        },
        onBeforeContentExpand,
      }),
    }))
    .filter((entry) => {
      // A placeholder costs a line, not a screenful, so it is shown wherever it
      // fell; only renderable content is rationed.
      if (!entry.resolved) return true;
      // A set counts as one block — the server caps it the same way — and the
      // return row it writes counts as none: it is an event, not content.
      if (entry.block.kind === 'set_result') return true;

      resolvedCount += 1;
      return resolvedCount <= MAX_CHAT_BLOCKS_PER_TURN;
    })
    .filter((entry) => entry.block.placement === placement);

  if (shown.length === 0) return null;

  /**
   * Event rows are the whole message they arrive in: a `set_result` row has no
   * prose above or below it, so the slot's own margin would only widen the gap
   * the thread already puts between messages.
   */
  const isEventOnly = shown.every(({ block }) => block.kind === 'set_result');
  const renderableByKind = new Map<string, typeof shown>();

  shown.forEach((entry) => {
    if (!entry.resolved) return;
    // Event lines are never collapsed behind a reveal chip; they are one line
    // each and reading them in order is the point.
    if (entry.block.kind === 'set_result') return;
    /**
     * Nor are study sets. A set is a launcher, not one item of a quiz to work
     * through in turn, and each one the server served is a session the student
     * was told about: a second set tucked behind a "Study Set 2" pill read as
     * never served (ORA-1118, a 28- and a 60-question set in one answer).
     */
    if (entry.block.kind === 'study_set') return;
    const group = renderableByKind.get(entry.block.kind) ?? [];
    group.push(entry);
    renderableByKind.set(entry.block.kind, group);
  });

  const progressiveBlockIds = new Set<string>();

  renderableByKind.forEach((entries) => {
    if (entries.length < 2) return;
    entries.slice(1).forEach(({ block }) => progressiveBlockIds.add(block.id));
  });

  const reveal = (blockId: string) => {
    onBeforeContentExpand?.();
    setOpenedBlockIds((current) => new Set(current).add(blockId));
    setFocusBlockId(blockId);
  };

  return (
    <div
      className={
        isEventOnly
          ? 'flex flex-col gap-1'
          : `flex flex-col gap-3 ${placement === 'inline' ? 'mb-3' : 'mt-3'}`
      }
      data-testid={`chat-blocks-${placement}`}
    >
      {shown.map(({ block, resolved }) => {
        const isHidden =
          progressiveBlockIds.has(block.id) && !openedBlockIds.has(block.id);

        return (
          <ChatBlockView
            key={block.id}
            block={block}
            resolved={resolved}
            shouldFocus={!isHidden && focusBlockId === block.id}
            isHidden={isHidden}
          />
        );
      })}
      {[...renderableByKind.entries()].map(([kind, entries]) => {
        if (entries.length < 2) return null;

        return (
          <div
            key={kind}
            className="flex flex-wrap gap-2"
            aria-label={`${blockKindLabel(kind)} choices`}
            role="group"
          >
            {entries.slice(1).map(({ block }, index) => {
              const opened = openedBlockIds.has(block.id);

              const answered =
                answeredBlockIds.has(block.id) || payloadHasAnswer(block);

              const label = `${blockKindLabel(kind)} ${index + 2}`;

              const accessibleLabel = answered
                ? `${label}, answered`
                : opened
                  ? `${label}, opened`
                  : `Reveal ${label}`;

              return (
                <button
                  key={block.id}
                  type="button"
                  aria-label={accessibleLabel}
                  aria-pressed={opened}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    answered
                      ? 'border-status-success text-status-success'
                      : opened
                        ? 'border-fg-secondary bg-surface-muted text-fg-primary'
                        : 'border-border-subtle bg-surface-muted text-fg-secondary hover:text-fg-primary'
                  }`}
                  onClick={() => reveal(block.id)}
                >
                  {label}
                  {answered ? <span aria-hidden="true"> ✓</span> : null}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default ChatBlocks;
